import * as Yup from 'yup';
import {
  validationConfigDefaults,
  hcpSignupAcceptExtAllowedMimeTypes,
} from './hcpSignupDefaults';
import {
  normalizeWizardDateInput,
  todayIsoDateLocal,
} from './hcpSignupDateUtils';

const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

function buildNamePattern(min, max) {
  return new RegExp(`^[A-Za-z\\s\\-']{${min},${max}}$`);
}

function buildIdNumberPattern(mode, min, max) {
  if (mode === 'numeric') {
    return new RegExp(`^[0-9]{${min},${max}}$`);
  }
  return new RegExp(`^[A-Za-z0-9\\-]{${min},${max}}$`);
}

function buildZipPattern(minLen, maxLen) {
  return new RegExp(`^[A-Za-z0-9\\s\\-]{${minLen},${maxLen}}$`);
}

export function validateIdFile(file, opts) {
  const maxMb =
    opts && Number.isFinite(Number(opts.maxMb)) ? Number(opts.maxMb) : 10;
  const acceptExt =
    Array.isArray(opts?.acceptExt) && opts.acceptExt.length
      ? opts.acceptExt
      : ['pdf', 'jpg', 'jpeg', 'png'];

  if (!file) return 'Choose a file';
  const name = (file.name || '').toLowerCase();
  const extOk = acceptExt.some((ext) => {
    const e = ext.replace(/^\./, '').toLowerCase();
    if (e === 'jpeg' || e === 'jpg') {
      return /\.(jpe?g)$/i.test(name);
    }
    return new RegExp(`\\.${e}$`, 'i').test(name);
  });

  const allowedMimes = hcpSignupAcceptExtAllowedMimeTypes(acceptExt);
  const mimeOk = (() => {
    const t = (file.type || '').toLowerCase();
    if (!t) return extOk;
    return allowedMimes.has(t);
  })();

  if (!extOk || !mimeOk)
    return `Only ${acceptExt.join(', ').toUpperCase()} are allowed`;
  if (file.size > maxMb * 1024 * 1024) return `Maximum file size is ${maxMb}MB`;
  return null;
}

/** @param {object} config merged validation config */
export function getIdImageValidationOptsFromConfig(config = {}) {
  const c = { ...validationConfigDefaults, ...config };
  return {
    maxMb: c.idImageMaxMb,
    acceptExt: c.idImageAcceptExt,
  };
}

/**
 * @param {object} config
 * @param {{ requirePortalPassword?: boolean }} [options] - when false, password change is optional (draft already has hash)
 */
export function buildGeneralInfoSchema(config, options = {}) {
  const c = { ...validationConfigDefaults, ...config };
  const requirePortalPassword = options.requirePortalPassword !== false;
  const requireCategory = options.requireCategory === true;

  const firstRe = buildNamePattern(c.firstNameMinLength, c.firstNameMaxLength);
  const lastRe = buildNamePattern(c.lastNameMinLength, c.lastNameMaxLength);

  const idRe = buildIdNumberPattern(
    String(c.idNumberPattern || 'alphanumeric').toLowerCase(),
    c.idNumberMinLength,
    c.idNumberMaxLength
  );

  const zipRe = buildZipPattern(c.addressZipMinLength, c.addressZipMaxLength);

  const idFileOpts = getIdImageValidationOptsFromConfig(c);

  const passwordField = requirePortalPassword
    ? Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
          PASSWORD_REGEX,
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        )
    : Yup.string()
        .transform((v) => (v == null ? '' : String(v)))
        .test(
          'password-strength',
          'Password must be at least 8 characters',
          (v) => !v || v.length >= 8
        )
        .test(
          'password-pattern',
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
          (v) => !v || PASSWORD_REGEX.test(v)
        );

  const confirmField = requirePortalPassword
    ? Yup.string()
        .required('Confirm password is required')
        .oneOf([Yup.ref('password')], 'Passwords must match')
    : Yup.string()
        .transform((v) => (v == null ? '' : String(v)))
        .test(
          'confirm-password',
          'Passwords must match',
          function matchConfirm(v) {
            const p = this.parent.password;
            if (!p && !v) return true;
            return v === p;
          }
        )
        .test(
          'confirm-required-when-password',
          'Confirm password is required',
          function confirmRequired(v) {
            const p = this.parent.password;
            if (!p) return true;
            return Boolean(v && v.trim());
          }
        );

  const streetSchema = c.addressStreetRequired
    ? Yup.string()
        .trim()
        .required('Street is required')
        .min(
          c.addressStreetMinLength,
          `Street must be ${c.addressStreetMinLength}–${c.addressStreetMaxLength} characters`
        )
        .max(c.addressStreetMaxLength)
    : Yup.string()
        .trim()
        .nullable()
        .transform((v) => (v == null ? '' : v))
        .test(
          'street-len',
          `Street must be ${c.addressStreetMinLength}–${c.addressStreetMaxLength} characters when provided`,
          (v) => {
            if (!v || !String(v).trim()) return true;
            const s = String(v).trim();
            return (
              s.length >= c.addressStreetMinLength &&
              s.length <= c.addressStreetMaxLength
            );
          }
        );

  const zipSchema = c.addressZipRequired
    ? Yup.string()
        .trim()
        .required('Zip / postal code is required')
        .matches(
          zipRe,
          `Use ${c.addressZipMinLength}–${c.addressZipMaxLength} characters (letters, digits, spaces, hyphens)`
        )
    : Yup.string()
        .trim()
        .nullable()
        .transform((v) => (v == null ? '' : v))
        .test(
          'zip-len',
          `Zip must be ${c.addressZipMinLength}–${c.addressZipMaxLength} characters when provided`,
          (v) => {
            if (!v || !String(v).trim()) return true;
            return zipRe.test(String(v).trim());
          }
        );

  return Yup.object({
    firstName: Yup.string()
      .trim()
      .required('First name is required')
      .matches(
        firstRe,
        `Use ${c.firstNameMinLength}–${c.firstNameMaxLength} characters: letters, spaces, hyphens, or apostrophes`
      ),

    lastName: Yup.string()
      .trim()
      .required('Last name is required')
      .matches(
        lastRe,
        `Use ${c.lastNameMinLength}–${c.lastNameMaxLength} characters: letters, spaces, hyphens, or apostrophes`
      ),

    email: Yup.string()
      .trim()
      .required('Email is required')
      .email('Invalid email address'),

    ...(requireCategory
      ? {
          categoryId: Yup.string()
            .required('Category is required')
            .test(
              'category-id',
              'Category is required',
              (v) =>
                /^\d+$/.test(String(v || '')) && parseInt(String(v), 10) > 0
            ),
        }
      : {}),

    dateOfBirth: Yup.string()
      .transform((v) => (v == null ? '' : normalizeWizardDateInput(String(v))))
      .required('Date of birth is required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Enter date as YYYY-MM-DD')
      .test('past', 'Date of birth cannot be after today', (value) =>
        Boolean(value && value <= todayIsoDateLocal())
      )
      .test(
        'min-age',
        `You must be at least ${c.dobMinAgeYears} years old`,
        (value) => {
          if (!c.dobMinAgeYears || c.dobMinAgeYears <= 0) return true;
          if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
          const [y, mo, d] = value.split('-').map((x) => parseInt(x, 10));
          const today = new Date();
          let age = today.getFullYear() - y;
          const tm = today.getMonth() + 1;
          const td = today.getDate();
          if (tm < mo || (tm === mo && td < d)) age -= 1;
          return age >= c.dobMinAgeYears;
        }
      ),

    nationality: Yup.string().required('Nationality is required'),

    idNumber: Yup.string()
      .trim()
      .required('ID number is required')
      .matches(
        idRe,
        c.idNumberPattern === 'numeric'
          ? `Use ${c.idNumberMinLength}–${c.idNumberMaxLength} digits`
          : `Use ${c.idNumberMinLength}–${c.idNumberMaxLength} characters: letters, digits, or hyphens`
      ),

    countryCode: Yup.string().required('Country code is required'),

    phoneNumber: Yup.string()
      .required('Phone number is required')
      .test(
        'digits',
        `Enter ${c.phoneMinDigits}–${c.phoneMaxDigits} digits`,
        (v) => {
          const d = String(v || '').replace(/\D/g, '');
          return d.length >= c.phoneMinDigits && d.length <= c.phoneMaxDigits;
        }
      ),

    password: passwordField,

    confirmPassword: confirmField,

    gender: c.genderRequired
      ? Yup.string().required('Gender is required')
      : Yup.string().nullable(),

    addressCountry: c.addressCountryRequired
      ? Yup.string().required('Country is required')
      : Yup.string().nullable(),

    addressState: c.addressStateRequired
      ? Yup.string().required('State / region is required')
      : Yup.string().nullable(),

    addressCity: c.addressCityRequired
      ? Yup.string().required('City is required')
      : Yup.string().nullable(),

    addressStreet: streetSchema,

    addressZipCode: zipSchema,

    idImageFile: Yup.mixed().nullable(),

    existingIdImageId: Yup.number().nullable(),
  })
    .test(
      'id-image',
      `ID document is required (${String(idFileOpts.acceptExt.join(', ')).toUpperCase()} — maximum ${idFileOpts.maxMb}MB)`,
      function validateId(vals) {
        if (!c.idImageRequired) return true;
        const hasFile = vals.idImageFile instanceof File;
        if (vals.existingIdImageId || hasFile) return true;
        const msg = `ID document is required (${String(idFileOpts.acceptExt.join(', ')).toUpperCase()} — maximum ${idFileOpts.maxMb}MB)`;
        return this.createError({ path: 'idImageFile', message: msg });
      }
    )
    .test(
      'id-image-file-valid',
      'Invalid ID document',
      function validateIdFileShape(vals) {
        if (!(vals.idImageFile instanceof File)) return true;
        const err = validateIdFile(vals.idImageFile, idFileOpts);
        if (err) return this.createError({ path: 'idImageFile', message: err });
        return true;
      }
    );
}

/**
 * License-tracking sign-up: account fields only (matches legacy `/enduser/register`).
 * @param {object} config
 * @param {{ requirePortalPassword?: boolean, requireCategory?: boolean, requireTerms?: boolean }} [options]
 */
export function buildMinimalAccountSignupSchema(config, options = {}) {
  const c = { ...validationConfigDefaults, ...config };
  const requirePortalPassword = options.requirePortalPassword !== false;
  const requireCategory = options.requireCategory !== false;
  const requireTerms = options.requireTerms !== false;

  const firstRe = buildNamePattern(c.firstNameMinLength, c.firstNameMaxLength);
  const lastRe = buildNamePattern(c.lastNameMinLength, c.lastNameMaxLength);

  const passwordField = requirePortalPassword
    ? Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
          PASSWORD_REGEX,
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        )
    : Yup.string()
        .transform((v) => (v == null ? '' : String(v)))
        .test(
          'password-strength',
          'Password must be at least 8 characters',
          (v) => !v || v.length >= 8
        )
        .test(
          'password-pattern',
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
          (v) => !v || PASSWORD_REGEX.test(v)
        );

  const confirmField = requirePortalPassword
    ? Yup.string()
        .required('Confirm password is required')
        .oneOf([Yup.ref('password')], 'Passwords must match')
    : Yup.string()
        .transform((v) => (v == null ? '' : String(v)))
        .test(
          'confirm-password',
          'Passwords must match',
          function matchConfirm(v) {
            const p = this.parent.password;
            if (!p && !v) return true;
            return v === p;
          }
        )
        .test(
          'confirm-required-when-password',
          'Confirm password is required',
          function confirmRequired(v) {
            const p = this.parent.password;
            if (!p) return true;
            return Boolean(v && v.trim());
          }
        );

  return Yup.object({
    firstName: Yup.string()
      .trim()
      .required('First name is required')
      .matches(
        firstRe,
        `Use ${c.firstNameMinLength}–${c.firstNameMaxLength} characters: letters, spaces, hyphens, or apostrophes`
      ),
    lastName: Yup.string()
      .trim()
      .required('Last name is required')
      .matches(
        lastRe,
        `Use ${c.lastNameMinLength}–${c.lastNameMaxLength} characters: letters, spaces, hyphens, or apostrophes`
      ),
    email: Yup.string()
      .trim()
      .required('Email is required')
      .email('Invalid email address'),
    ...(requireCategory
      ? {
          categoryId: Yup.string()
            .required('Category is required')
            .test(
              'category-id',
              'Category is required',
              (v) =>
                /^\d+$/.test(String(v || '')) && parseInt(String(v), 10) > 0
            ),
        }
      : {}),
    password: passwordField,
    confirmPassword: confirmField,
    ...(requireTerms
      ? {
          tnc: Yup.boolean().oneOf(
            [true],
            'You must accept the Terms and Conditions'
          ),
        }
      : {}),
  });
}

/** Customer profile (license tracking): editable account identity fields only. */
export function buildMinimalAccountProfileSchema(config) {
  const c = { ...validationConfigDefaults, ...config };
  const firstRe = buildNamePattern(c.firstNameMinLength, c.firstNameMaxLength);
  const lastRe = buildNamePattern(c.lastNameMinLength, c.lastNameMaxLength);

  return Yup.object({
    firstName: Yup.string()
      .trim()
      .required('First name is required')
      .matches(
        firstRe,
        `Use ${c.firstNameMinLength}–${c.firstNameMaxLength} characters: letters, spaces, hyphens, or apostrophes`
      ),
    lastName: Yup.string()
      .trim()
      .required('Last name is required')
      .matches(
        lastRe,
        `Use ${c.lastNameMinLength}–${c.lastNameMaxLength} characters: letters, spaces, hyphens, or apostrophes`
      ),
    email: Yup.string()
      .trim()
      .required('Email is required')
      .email('Invalid email address'),
  });
}
